import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  created_at: string;
  status: string;
  has_generated?: boolean;
  generated_count?: number;
  generation_status?: string;
}

interface GenerationResult {
  processed: number;
  failed: number;
  total: number;
  errors: string[];
}

export function useTravelBytesGenerator() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);

  const fetchBlogPosts = async () => {
    setIsLoadingPosts(true);
    try {
      // Fetch all published blog posts
      const { data: posts, error: postsError } = await supabase
        .from('blog_posts')
        .select('id, title, slug, created_at, status')
        .eq('status', 'published')
        .order('created_at', { ascending: false });

      if (postsError) throw postsError;

      // Fetch generation tracking info
      const { data: generated, error: genError } = await supabase
        .from('blog_travel_byte_generation')
        .select('blog_post_id, status, generated_count');

      if (genError) throw genError;

      // Mark which posts have been generated
      const generatedMap = new Map(
        generated?.map(g => [g.blog_post_id, g]) || []
      );

      const postsWithStatus = posts?.map(post => ({
        ...post,
        has_generated: generatedMap.has(post.id),
        generated_count: generatedMap.get(post.id)?.generated_count || 0,
        generation_status: generatedMap.get(post.id)?.status
      })) || [];

      setBlogPosts(postsWithStatus);
      return postsWithStatus;
    } catch (error: any) {
      console.error('Error fetching blog posts:', error);
      toast.error('Failed to load blog posts');
      return [];
    } finally {
      setIsLoadingPosts(false);
    }
  };

  const generateTravelBytes = async (blogPostIds: string[]) => {
    setIsGenerating(true);
    try {
      console.log('Generating travel bytes for posts:', blogPostIds);
      
      const { data, error } = await supabase.functions.invoke('generate-travel-bytes', {
        body: { blogPostIds }
      });

      if (error) {
        console.error('Function invocation error:', error);
        throw error;
      }

      const result = data as { success: boolean; results?: GenerationResult; error?: string };
      
      if (!result.success) {
        throw new Error(result.error || 'Generation failed');
      }

      const { results } = result;
      if (!results) {
        throw new Error('No results returned');
      }

      console.log('Generation results:', results);

      // Show results
      if (results.processed > 0) {
        toast.success(
          `Successfully generated travel bytes from ${results.processed} blog post(s)!`
        );
      }
      
      if (results.failed > 0) {
        toast.error(
          `Failed to process ${results.failed} blog post(s). Check console for details.`
        );
        console.error('Generation errors:', results.errors);
      }

      // Refresh the posts list
      await fetchBlogPosts();

      return results;
    } catch (error: any) {
      console.error('Error generating travel bytes:', error);
      
      if (error.message?.includes('Rate limit')) {
        toast.error('Rate limit exceeded. Please try again later.');
      } else if (error.message?.includes('Payment required')) {
        toast.error('AI credits exhausted. Please add credits in Settings → Workspace → Usage.');
      } else {
        toast.error(`Failed to generate travel bytes: ${error.message}`);
      }
      
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    blogPosts,
    isLoadingPosts,
    isGenerating,
    fetchBlogPosts,
    generateTravelBytes
  };
}